import { motion } from "framer-motion";

const CHAPTERS = [
  {
    num: "01",
    title: "Delivery of quality, in time",
    text: "We believe in uncompromising quality delivered on schedule. Every project — from solar plants to medical installations — is executed with precision and accountability.",
  },
  {
    num: "02",
    title: "Engineering-led execution",
    text: "Led by seasoned engineers and project managers, our approach blends deep design engineering with new technology transfer across renewable energy and healthcare.",
  },
  {
    num: "03",
    title: "Trusted across sectors",
    text: "We have delivered for Government, PSUs, corporates, private organisations, hospitals and medical colleges — building a reputation for reliability in Odisha and beyond.",
  },
  {
    num: "04",
    title: "Building what comes next",
    text: "We are expanding into manufacturing of medical consumables and machinery — deepening our capability to power a sustainable, self-reliant future.",
  },
];

export default function WhoWeAre() {
  return (
    <section id="who-we-are" className="py-28 lg:py-40 border-t border-white/10" data-testid="who-we-are-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-mono-accent text-xs tracking-[0.35em] uppercase text-neon mb-6"
          >
            Who We Are
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[1.02]"
            data-testid="who-we-are-heading"
          >
            Trust builds businesses. Relationships outlast margins.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base md:text-lg text-slate-400 leading-relaxed mt-8 max-w-lg"
          >
            GENOUM Private Limited is a diversified group with a simple belief: progress
            means honoring tradition while moving beyond it. Over five years, we&apos;ve
            delivered across renewable energy, EPC, and medical equipment and devices.
            Now, as we expand into medical consumables and machinery manufacturing, we
            remain committed to one goal — a self-reliant, sustainable future.
          </motion.p>
          <motion.figure
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="mt-14 img-zoom border border-white/10"
          >
            <div className="overflow-hidden">
              <img
                src="/assets/office-building.jpg"
                alt="GENOUM GROUP corporate office, Bhubaneswar"
                className="w-full h-72 lg:h-96 object-cover"
                loading="lazy"
              />
            </div>
            <figcaption className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-slate-500 px-5 py-4 border-t border-white/10 bg-navy-800/40">
              Corporate Office — Bhubaneswar, Odisha
            </figcaption>
          </motion.figure>
        </div>

        <div className="flex flex-col justify-center">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.num}
              initial={{ opacity: 0, x: 48 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="border-t border-white/10 py-10 first:border-t-0 lg:first:border-t group"
              data-testid={`chapter-${c.num}`}
            >
              <div className="flex items-baseline gap-6">
                <span className="font-mono-accent text-sm text-electric group-hover:text-neon transition-colors duration-300">
                  {c.num}
                </span>
                <div>
                  <h3 className="font-display text-xl lg:text-2xl font-bold uppercase tracking-tight group-hover:text-neon transition-colors duration-300">
                    {c.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed mt-3">{c.text}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
